const NULLISH_RECIPE_VALUES = new Set(["", "null", "undefined"]);

const normalizeRecipeValue = (value) => String(value ?? "").trim();

const isNullishRecipeValue = (value) =>
  NULLISH_RECIPE_VALUES.has(normalizeRecipeValue(value).toLowerCase());

export const normalizeCardName = (value) =>
  normalizeRecipeValue(value).toLowerCase();

export const isFiniteCard = (card) => {
  const raw = card?.finite;
  if (typeof raw === "string") return raw.trim().toLowerCase() === "true";
  return !!raw;
};

const splitRecipeField = (value) => {
  if (isNullishRecipeValue(value)) return [];
  return String(value)
    .split(",")
    .map((part) => part.trim());
};

export const parseCraftRecipeFields = (craftNeeded, craftAmount) => {
  const names = splitRecipeField(craftNeeded);
  const amounts = splitRecipeField(craftAmount);

  if (!names.length && !amounts.length) {
    return {
      valid: false,
      materials: [],
      errors: ["Recipe is missing."],
    };
  }

  if (!names.length || !amounts.length || names.length !== amounts.length) {
    return {
      valid: false,
      materials: [],
      errors: ["Recipe names and amounts must line up."],
    };
  }

  const materials = [];
  for (let index = 0; index < names.length; index += 1) {
    const name = names[index];
    const parsedAmount = Number.parseInt(amounts[index], 10);

    if (!name) {
      return {
        valid: false,
        materials: [],
        errors: ["Recipe contains a blank material name."],
      };
    }

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return {
        valid: false,
        materials: [],
        errors: [`Recipe amount for "${name}" must be a positive integer.`],
      };
    }

    materials.push({
      name,
      amount: parsedAmount,
    });
  }

  return {
    valid: true,
    materials,
    errors: [],
  };
};

export const parseCraftRecipe = (card) =>
  parseCraftRecipeFields(card?.craftNeeded, card?.craftAmount);

export const createEmptyRecipeRow = () => ({
  name: "",
  amount: "1",
});

export const validateCraftRecipeRows = (rows = []) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const cleanedRows = [];

  for (const row of normalizedRows) {
    const name = normalizeRecipeValue(row?.name);
    const amount = normalizeRecipeValue(row?.amount);

    if (!name && !amount) continue;

    if (!name || !amount) {
      return {
        valid: false,
        rows: [],
        error: "Each recipe row needs both a material card and an amount.",
      };
    }

    const parsedAmount = Number.parseInt(amount, 10);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return {
        valid: false,
        rows: [],
        error: `Recipe amount for "${name}" must be a positive integer.`,
      };
    }

    cleanedRows.push({
      name,
      amount: String(parsedAmount),
    });
  }

  return {
    valid: true,
    rows: cleanedRows,
    error: "",
  };
};

export const serializeCraftRecipeRows = (rows = []) => {
  const validation = validateCraftRecipeRows(rows);
  if (!validation.valid) {
    return {
      valid: false,
      craftNeeded: "",
      craftAmount: "",
      rows: [],
      error: validation.error,
    };
  }

  return {
    valid: true,
    craftNeeded: validation.rows.map((row) => row.name).join(", "),
    craftAmount: validation.rows.map((row) => row.amount).join(", "),
    rows: validation.rows,
    error: "",
  };
};

export const deserializeCraftRecipeRows = (craftNeeded, craftAmount) => {
  const parsed = parseCraftRecipeFields(craftNeeded, craftAmount);
  if (parsed.valid) {
    return parsed.materials.map((material) => ({
      name: material.name,
      amount: String(material.amount),
    }));
  }

  const names = splitRecipeField(craftNeeded);
  const amounts = splitRecipeField(craftAmount);
  const rowCount = Math.max(names.length, amounts.length);
  const rows = [];

  for (let index = 0; index < rowCount; index += 1) {
    rows.push({
      name: names[index] || "",
      amount: amounts[index] || "1",
    });
  }

  return rows;
};

export const getReservedCopiesForCard = (cardName, decks = []) => {
  const normalizedName = normalizeCardName(cardName);
  if (!normalizedName || !Array.isArray(decks)) return 0;

  return decks.reduce((maxReserved, deck) => {
    const deckCards = Array.isArray(deck?.cards) ? deck.cards : [];
    const countInDeck = deckCards.filter(
      (card) => normalizeCardName(card?.name) === normalizedName,
    ).length;
    return Math.max(maxReserved, countInDeck);
  }, 0);
};

export const getOwnedCardByName = (playerCards = [], cardName) => {
  const normalizedName = normalizeCardName(cardName);
  return (Array.isArray(playerCards) ? playerCards : []).find(
    (card) => normalizeCardName(card?.name) === normalizedName,
  );
};

export const getCatalogCardByName = (cardCatalog = [], cardName) => {
  const normalizedName = normalizeCardName(cardName);
  return (Array.isArray(cardCatalog) ? cardCatalog : []).find(
    (card) => normalizeCardName(card?.name) === normalizedName,
  );
};

const buildMaterialTotals = (materials = []) => {
  const totals = new Map();

  materials.forEach((material) => {
    const key = normalizeCardName(material.name);
    const current = totals.get(key) || {
      name: material.name,
      requiredAmount: 0,
    };

    current.requiredAmount += material.amount;
    totals.set(key, current);
  });

  return totals;
};

export const buildCraftingAvailability = ({
  card,
  playerCards = [],
  decks = [],
  cardCatalog = [],
} = {}) => {
  const parsedRecipe = parseCraftRecipe(card);
  if (!parsedRecipe.valid) {
    return {
      valid: false,
      canCraft: false,
      errors: parsedRecipe.errors,
      materials: [],
      expandedSlots: [],
      totalsByName: new Map(),
    };
  }

  const totalsByName = buildMaterialTotals(parsedRecipe.materials);
  const expandedCountsByName = new Map();
  const materials = parsedRecipe.materials.map((material, index) => {
    const ownedCard = getOwnedCardByName(playerCards, material.name);
    const catalogCard =
      getCatalogCardByName(cardCatalog, material.name) || ownedCard || null;
    const ownedCopies = Number(ownedCard?.amountOwned || 0);
    const reservedCopies = getReservedCopiesForCard(material.name, decks);
    const availableCopies = Math.max(0, ownedCopies - reservedCopies);
    const requiredAmount =
      totalsByName.get(normalizeCardName(material.name))?.requiredAmount || 0;

    return {
      ...material,
      key: `${normalizeCardName(material.name)}-${index}`,
      ownedCard,
      card: catalogCard,
      ownedCopies,
      reservedCopies,
      availableCopies,
      requiredAmount,
    };
  });

  const expandedSlots = [];
  materials.forEach((material, materialIndex) => {
    const normalizedName = normalizeCardName(material.name);

    for (let copyIndex = 0; copyIndex < material.amount; copyIndex += 1) {
      const usageCount = expandedCountsByName.get(normalizedName) || 0;
      const orderIndex = usageCount + 1;
      expandedCountsByName.set(normalizedName, orderIndex);

      expandedSlots.push({
        id: `${material.key}-slot-${copyIndex}`,
        materialIndex,
        copyIndex,
        orderIndex,
        name: material.name,
        card: material.card,
        ownedCard: material.ownedCard,
        availableCopies: material.availableCopies,
        reservedCopies: material.reservedCopies,
        ownedCopies: material.ownedCopies,
        isAvailable: material.availableCopies >= orderIndex,
      });
    }
  });

  const canCraft = Array.from(totalsByName.values()).every((entry) => {
    const ownedCard = getOwnedCardByName(playerCards, entry.name);
    const ownedCopies = Number(ownedCard?.amountOwned || 0);
    const reservedCopies = getReservedCopiesForCard(entry.name, decks);
    const availableCopies = Math.max(0, ownedCopies - reservedCopies);
    return availableCopies >= entry.requiredAmount;
  });

  return {
    valid: true,
    canCraft,
    errors: [],
    materials,
    expandedSlots,
    totalsByName,
  };
};

export const getCraftableCountFromAvailability = (availability) => {
  if (!availability?.valid) return 0;

  const totals = Array.from(availability.totalsByName.values());
  if (!totals.length) return 0;

  return totals.reduce((lowest, entry) => {
    const material = availability.materials.find(
      (item) => normalizeCardName(item.name) === normalizeCardName(entry.name),
    );
    if (!material) return 0;

    const craftableCopies = Math.floor(
      Math.max(0, material.availableCopies) / entry.requiredAmount,
    );

    return Math.min(lowest, craftableCopies);
  }, Number.POSITIVE_INFINITY);
};
